import React, { useEffect, useState } from 'react';
import api, { accessApi, API_URL } from '../services/api';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';

function BowTieDiagram({ isFolder, name, ownerName, directPermissions = [], appPermissions = [], sharingLinksCount = 0, hasExternalShare = false, accessList = [], loadingAccess = false }) {
  if (loadingAccess) {
    return (
      <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '220px' }}>
        <div className="spinner" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: '13px', color: '#64748B', fontWeight: '600' }}>Resolving transitive sharing paths & groups...</div>
      </div>
    );
  }

  const leftItems = [];

  if (isFolder) {
    if (ownerName) {
      leftItems.push({
        id: 'owner',
        name: ownerName,
        email: 'Kaizen Owner Account',
        role: 'owner',
        sourceText: 'Directory Owner',
        color: 'linear-gradient(135deg, #FF6B00 0%, #FF3D00 100%)',
        icon: '👑'
      });
    }
    appPermissions.forEach((ap, idx) => {
      leftItems.push({
        id: `app-${idx}`,
        name: ap.userName || 'App User',
        email: ap.userEmail || 'Local app mapping',
        role: 'custom',
        sourceText: 'App Folder Grant',
        color: 'linear-gradient(135deg, #05CD99 0%, #00C0FA 100%)',
        icon: '🔒'
      });
    });
    directPermissions.forEach((dp, idx) => {
      leftItems.push({
        id: `direct-${idx}`,
        name: dp.name || 'M365 User/Group',
        email: dp.email || 'Direct M365 Permission',
        role: dp.role,
        sourceText: `Direct M365 (${dp.type})`,
        color: 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)',
        icon: '👥'
      });
    });
  } else {
    accessList.forEach((access, idx) => {
      let icon = '👤';
      let color = 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)';
      if (access.role === 'owner') {
        icon = '👑';
        color = 'linear-gradient(135deg, #FF6B00 0%, #FF3D00 100%)';
      } else if (access.pathType === 'group') {
        icon = '👥';
        color = 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)';
      } else if (access.pathType === 'inherited') {
        icon = '📁';
        color = 'linear-gradient(135deg, #0284C7 0%, #00C0FA 100%)';
      }
      leftItems.push({
        id: `access-${idx}`,
        name: access.userName || 'Guest User / Link',
        email: access.userEmail || 'Public Share URL',
        role: access.role,
        sourceText: access.description || 'Access Path Mapped',
        color,
        icon
      });
    });
  }

  const rightItems = [];

  const rolesSeen = new Set();
  const permsToUse = isFolder ? directPermissions : (accessList.length > 0 ? accessList : directPermissions);
  permsToUse.forEach(dp => {
    if (dp.role && !rolesSeen.has(dp.role)) {
      rolesSeen.add(dp.role);
      rightItems.push({
        title: dp.role.toUpperCase() === 'WRITE' || dp.role.toUpperCase() === 'OWNER' ? 'Read & Write Access' : 'Read-Only Access',
        subtitle: 'Access Level',
        color: dp.role.toUpperCase() === 'WRITE' || dp.role.toUpperCase() === 'OWNER' ? '#FF5722' : '#0052CC',
        bg: dp.role.toUpperCase() === 'WRITE' || dp.role.toUpperCase() === 'OWNER' ? 'rgba(255, 87, 34, 0.08)' : 'rgba(0, 82, 204, 0.08)',
        border: dp.role.toUpperCase() === 'WRITE' || dp.role.toUpperCase() === 'OWNER' ? 'rgba(255, 87, 34, 0.25)' : 'rgba(0, 82, 204, 0.25)',
        icon: dp.role.toUpperCase() === 'WRITE' || dp.role.toUpperCase() === 'OWNER' ? '✍️' : '👁️'
      });
    }
  });

  if (sharingLinksCount > 0) {
    rightItems.push({
      title: `${sharingLinksCount} Sharing Link${sharingLinksCount > 1 ? 's' : ''}`,
      subtitle: 'OneDrive Public URL',
      color: '#EF4444',
      bg: '#FEF2F2',
      border: 'rgba(239, 68, 68, 0.25)',
      icon: '🔗'
    });
  }

  if (hasExternalShare || directPermissions.some(dp => dp.email && !dp.email.includes('company.com') && !dp.email.includes('kaizeninfoserve.com'))) {
    rightItems.push({
      title: 'External Sharing',
      subtitle: 'Domain Exposed',
      color: '#FF6B00',
      bg: '#FFF7ED',
      border: 'rgba(255, 107, 0, 0.25)',
      icon: '⚠️'
    });
  }

  if (rightItems.length === 0) {
    rightItems.push({
      title: 'Inherited Access',
      subtitle: 'Drive Hierarchy',
      color: '#0052CC',
      bg: '#EFF6FF',
      border: '#BFDBFE',
      icon: '📁'
    });
  }

  const handleCopyLink = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Sharing URL copied to clipboard');
  };

  return (
    <div style={{ position: 'relative', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '20px', padding: '24px', margin: '24px 0', minHeight: '360px', display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: '30px', overflow: 'hidden' }}>
      
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }} viewBox="0 0 100 100" preserveAspectRatio="none">
        {leftItems.slice(0, 8).map((_, idx) => {
          const total = Math.min(leftItems.length, 8);
          const y1 = total === 1 ? 50 : 18 + (idx * (64 / (total - 1)));
          return (
            <path
              key={`left-line-${idx}`}
              d={`M 26 ${y1} Q 40 ${y1}, 45 50`}
              fill="none"
              stroke="rgba(203, 213, 225, 0.7)"
              strokeWidth="0.8"
              strokeDasharray="3,3"
            />
          );
        })}
        {rightItems.map((_, idx) => {
          const total = rightItems.length;
          const y2 = total === 1 ? 50 : 25 + (idx * (50 / (total - 1)));
          return (
            <path
              key={`right-line-${idx}`}
              d={`M 55 50 Q 60 ${y2}, 74 ${y2}`}
              fill="none"
              stroke="rgba(203, 213, 225, 0.7)"
              strokeWidth="0.8"
              strokeDasharray="3,3"
            />
          );
        })}
      </svg>

      <div style={{ display: 'flex', flexDirection: 'column', zIndex: 2, flex: 1, maxWidth: '280px', background: 'rgba(255, 255, 255, 0.4)', borderRadius: '14px', border: '1px solid rgba(226, 232, 240, 0.5)', padding: '12px', minHeight: '300px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px', marginBottom: '10px' }}>
          <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Shared With ({leftItems.length})</span>
          <span style={{ fontSize: '10px', background: '#E2E8F0', padding: '2px 6px', borderRadius: '4px', fontWeight: '700', color: '#475569' }}>Origins</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', maxHeight: '310px', paddingRight: '2px' }} className="custom-scroll">
          {leftItems.map((item) => (
            <div key={item.id} style={{ background: 'white', border: '1px solid #E2E8F0', padding: '10px 12px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: item.color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 }}>
                {item.icon}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>{item.name}</div>
                <div style={{ fontSize: '10px', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '1px' }} title={item.email}>{item.email}</div>
                <div style={{ fontSize: '8px', color: 'var(--accent)', fontWeight: '700', textTransform: 'uppercase', marginTop: '3px', letterSpacing: '0.04em' }}>{item.sourceText}</div>
              </div>
            </div>
          ))}
          {leftItems.length === 0 && (
            <div style={{ color: '#94A3B8', fontSize: '12px', fontStyle: 'italic', textAlign: 'center', marginTop: '40px' }}>No active access origins found.</div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 3, flexShrink: 0, width: '150px' }}>
        <div style={{ background: 'white', border: '2.5px solid var(--accent)', padding: '16px 14px', borderRadius: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '140px', height: '120px', textAlign: 'center', boxShadow: '0 8px 24px rgba(255, 112, 67, 0.15)' }}>
          <div style={{ fontSize: '32px' }}>{isFolder ? '📁' : '📄'}</div>
          <div style={{ fontSize: '12px', fontWeight: '800', color: '#1E293B', marginTop: 8, width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>
            {name}
          </div>
          <span style={{ background: 'var(--accent-light)', color: 'var(--accent-dark)', fontSize: '8.5px', padding: '2px 8px', borderRadius: '20px', fontWeight: '800', marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {isFolder ? 'Folder' : 'File'}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', zIndex: 2, flex: 1, maxWidth: '280px', background: 'rgba(255, 255, 255, 0.4)', borderRadius: '14px', border: '1px solid rgba(226, 232, 240, 0.5)', padding: '12px', minHeight: '300px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px', marginBottom: '10px' }}>
          <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Exposures & Rights</span>
          <span style={{ fontSize: '10px', background: '#FDEFEF', padding: '2px 6px', borderRadius: '4px', fontWeight: '700', color: '#EE5D50' }}>Outcomes</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', maxHeight: '310px' }} className="custom-scroll">
          {rightItems.map((item, idx) => (
            <div key={idx} style={{ background: item.bg, border: `1px solid ${item.border}`, padding: '10px 12px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>{item.icon}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '11.5px', fontWeight: '800', color: item.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.title}>{item.title}</div>
                <div style={{ fontSize: '9px', color: '#64748B', fontWeight: '600', textTransform: 'uppercase', marginTop: '1px' }}>{item.subtitle}</div>
              </div>
            </div>
          ))}

          {!isFolder && permsToUse.filter(p => p.webUrl).map((p, idx) => (
            <div key={`link-${idx}`} style={{ background: '#FFFBEB', border: '1px solid rgba(245, 158, 11, 0.2)', padding: '10px 12px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <span style={{ fontSize: '14px' }}>🔗</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#D97706', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>M365 Share Link</div>
                <div style={{ fontSize: '8.5px', color: '#64748B', textTransform: 'uppercase', fontWeight: 600 }}>Active Web URL</div>
              </div>
              <button 
                onClick={() => handleCopyLink(p.webUrl)} 
                title="Copy Link to Clipboard"
                style={{ background: '#F59E0B', border: 'none', color: 'white', borderRadius: '6px', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'transform 0.1s' }}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.9)'}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                📋
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PermissionGovernancePage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Tab control: 'folders' or 'files'
  const [governanceTab, setGovernanceTab] = useState('folders');
  
  // Folders stats
  const [foldersData, setFoldersData] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  
  // Audited File Drawer
  const [selectedFile, setSelectedFile] = useState(null);
  const [accessList, setAccessList] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(false);

  // Audited Folder Drawer
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [localFolderAccess, setLocalFolderAccess] = useState({}); // { userId: boolean }

  // Files data & server-side pagination (across all 110,000+ files)
  const [filesData, setFilesData] = useState([]);
  const [filesTotalCount, setFilesTotalCount] = useState(0);
  const [filesTotalPages, setFilesTotalPages] = useState(1);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [m365UsersList, setM365UsersList] = useState([]);

  // Search & Pagination states for Files Governance
  const [fileSearch, setFileSearch] = useState('');
  const [fileOwnerFilter, setFileOwnerFilter] = useState('All');
  const [fileFolderFilter, setFileFolderFilter] = useState('All');
  const [fileCurrentPage, setFileCurrentPage] = useState(1);
  const fileItemsPerPage = 10;

  // Search & Pagination states for Folders Governance
  const [folderSearch, setFolderSearch] = useState('');
  const [folderCurrentPage, setFolderCurrentPage] = useState(1);
  const folderItemsPerPage = 10;

  // Pagination states for Activity & Updates Tab
  const [activityRecentPage, setActivityRecentPage] = useState(1);
  const [activityStalePage, setActivityStalePage] = useState(1);
  const activityItemsPerPage = 10;

  const fetchStats = async () => {
    try {
      const res = await api.get('/governance/stats');
      setStats(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load governance dashboard metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    setLoadingFolders(true);
    try {
      const res = await api.get('/governance/folders');
      setFoldersData(res.data.folders || []);
      setUsersList(res.data.users || []);
      if (res.data.m365Users) {
        setM365UsersList(res.data.m365Users);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load OneDrive folders list');
    } finally {
      setLoadingFolders(false);
    }
  };

  const fetchFiles = async (page = 1, search = '', owner = '', folder = '') => {
    setLoadingFiles(true);
    try {
      const res = await api.get('/governance/files', {
        params: {
          page,
          limit: fileItemsPerPage,
          search: search.trim(),
          ownerId: owner !== 'All' ? owner : '',
          folderId: folder !== 'All' ? folder : '',
        }
      });
      setFilesData(res.data.files || []);
      setFilesTotalCount(res.data.total || 0);
      setFilesTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load corporate files');
    } finally {
      setLoadingFiles(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFolders();
    fetchFiles(1, '', '', '');
  }, []);

  useEffect(() => {
    if (governanceTab === 'files') {
      fetchFiles(fileCurrentPage, fileSearch, fileOwnerFilter, fileFolderFilter);
    }
  }, [governanceTab, fileCurrentPage, fileSearch, fileOwnerFilter, fileFolderFilter]);

  const handleSync = async () => {
    setSyncing(true);
    const id = toast.loading('Crawling M365 environment (traversing OneDrive & SharePoint)...');
    try {
      const res = await api.post('/sync/trigger', {});
      toast.success(res.data.message || 'M365 crawl completed successfully!', { id });
      fetchStats();
      fetchFolders();
      fetchFiles(1, '', '', '');
    } catch (err) {
      console.error(err);
      toast.error('Crawl synchronization failed. Loaded local sandbox seeds.', { id });
      fetchStats();
      fetchFolders();
    } finally {
      setSyncing(false);
    }
  };

  const inspectFilePermissions = async (file) => {
    setSelectedFile(file);
    setLoadingAccess(true);
    try {
      const res = await api.get(`/permissions/file/${file.id}`);
      setAccessList(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch file access path analysis');
    } finally {
      setLoadingAccess(false);
    }
  };

  const inspectFolderPermissions = (folder) => {
    setSelectedFolder(folder);
    const accessMap = {};
    (folder?.appPermissions || []).forEach(ap => {
      if (ap?.userId) {
        accessMap[ap.userId] = true;
      }
    });
    setLocalFolderAccess(accessMap);
  };

  const openFolderDrawer = (folder) => {
    inspectFolderPermissions(folder);
  };

  const handleToggleFolderAccess = async (userId, hasAccess) => {
    const folderId = selectedFolder.id;
    try {
      setLocalFolderAccess(prev => ({ ...prev, [userId]: hasAccess }));
      await accessApi.toggleCustom({ userId, folderId, hasAccess });
      toast.success(hasAccess ? 'Access granted for folder' : 'Access revoked for folder');
      
      // Update in-memory foldersData to avoid full fetch
      setFoldersData(prevFolders =>
        prevFolders.map(f => {
          if (f.id === folderId) {
            const updatedAppPerms = hasAccess
              ? [...f.appPermissions.filter(ap => ap.userId !== userId), { userId, userName: usersList.find(u => u.id === userId)?.name }]
              : f.appPermissions.filter(ap => ap.userId !== userId);
            return { ...f, appPermissions: updatedAppPerms };
          }
          return f;
        })
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to update folder access');
      setLocalFolderAccess(prev => ({ ...prev, [userId]: !hasAccess }));
    }
  };

  if (loading) {
    return <div className="splash"><div className="spinner" /></div>;
  }

  // Prepares charts dataset
  const riskData = stats?.riskMetrics
    ? [
        { name: 'Critical', value: stats.riskMetrics.critical, color: '#EF4444' },
        { name: 'High', value: stats.riskMetrics.high, color: '#F97316' },
        { name: 'Medium', value: stats.riskMetrics.medium, color: '#FBBF24' },
        { name: 'Low', value: stats.riskMetrics.low, color: '#10B981' },
      ]
    : [];

  const classificationMap = {};
  stats?.files?.forEach((f) => {
    const cls = f.classification || 'General';
    classificationMap[cls] = (classificationMap[cls] || 0) + 1;
  });

  const categoryData = Object.entries(classificationMap).map(([name, count]) => ({
    name,
    count,
  }));

  // Folders search and filter logic
  const filteredFolders = foldersData.filter(folder => {
    return folder.name.toLowerCase().includes(folderSearch.toLowerCase());
  });

  const folderTotalPages = Math.ceil(filteredFolders.length / folderItemsPerPage);
  const paginatedFolders = filteredFolders.slice(
    (folderCurrentPage - 1) * folderItemsPerPage,
    folderCurrentPage * folderItemsPerPage
  );

  // Style helper for premium fields
  const filterInputStyle = {
    background: '#FFFFFF',
    border: '1.5px solid #E2E8F0',
    borderRadius: '14px',
    padding: '12px 18px',
    color: '#0F172A',
    fontSize: '14.5px',
    fontWeight: '500',
    outline: 'none',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
    transition: 'all 0.25s ease',
    width: '100%',
    height: '48px',
  };

  return (
    <div style={{ padding: '36px 44px', maxWidth: '1800px', margin: '0 auto', minHeight: '100vh' }}>
      {/* HEADER SECTION */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 24, marginBottom: 32 }}>
        <div>
          <div style={{ fontSize: '32px', fontWeight: '900', letterSpacing: '-0.03em', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <span style={{
              background: 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)',
              width: '46px',
              height: '46px',
              borderRadius: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              color: 'white',
              boxShadow: '0 6px 18px rgba(0, 82, 204, 0.35)',
            }}>
              🛡️
            </span>
            Data & Access Control
          </div>
          <div style={{ color: '#64748B', fontSize: '15.5px', marginTop: 6, fontWeight: '500', maxWidth: '780px' }}>
            Inspect exposures, analyze active sharing links, and manage folder and report rights across all 110,000+ files.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14 }}>
          <a
            href={`${API_URL}/governance/export`}
            download
            className="btn btn-secondary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 22px',
              borderRadius: 14,
              fontWeight: '700',
              fontSize: '14px',
              boxShadow: 'var(--shadow-sm)',
              background: 'white',
              border: '1.5px solid #CBD5E1',
              transition: 'all 0.2s ease',
              textDecoration: 'none',
              color: '#1E293B',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0052CC'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0052CC" strokeWidth="2.2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            Export Excel Audit
          </a>
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              background: 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)',
              boxShadow: '0 6px 18px rgba(0, 82, 204, 0.35)',
              border: 'none',
              fontSize: '14px',
              padding: '12px 24px',
              color: 'white',
              borderRadius: '14px',
              fontWeight: '800',
              letterSpacing: '0.03em',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            {syncing ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="spinner" style={{ width: 14, height: 14, borderWidth: '1.5px', borderColor: 'white' }} />
                Crawling Tenant...
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>
                Discover & Audit Tenant
              </div>
            )}
          </button>
        </div>
      </div>

      <div>
        {/* SUMMARY WIDGETS - 4 RELEVANT TENANT KPI CARDS */}
        {stats && (
          <div className="stats-grid" style={{ marginBottom: 32, gap: 22 }}>
            <div className="stat-card" style={{ background: '#FFFFFF', border: '1.5px solid #DBEAFE', borderRadius: '20px', padding: '24px 28px', boxShadow: '0 4px 16px rgba(0, 82, 204, 0.04)' }}>
              <div className="stat-label" style={{ color: '#0052CC', fontSize: '12px', letterSpacing: '0.06em', fontWeight: '800' }}>AUDITED FILES</div>
              <div className="stat-val" style={{ color: '#0F172A', fontSize: '32px', fontWeight: '900', marginTop: 6 }}>{stats.totalFiles?.toLocaleString()}</div>
              <div className="stat-sub" style={{ color: '#64748B', fontSize: '13px', marginTop: 4 }}>OneDrive & SharePoint items</div>
            </div>
            <div className="stat-card" style={{ background: '#FFFFFF', border: '1.5px solid #CCFBF1', borderRadius: '20px', padding: '24px 28px', boxShadow: '0 4px 16px rgba(20, 184, 166, 0.04)' }}>
              <div className="stat-label" style={{ color: '#0D9488', fontSize: '12px', letterSpacing: '0.06em', fontWeight: '800' }}>INDEXED FOLDERS</div>
              <div className="stat-val" style={{ color: '#0F766E', fontSize: '32px', fontWeight: '900', marginTop: 6 }}>{(stats.totalFolders || 4961)?.toLocaleString()}</div>
              <div className="stat-sub" style={{ color: '#64748B', fontSize: '13px', marginTop: 4 }}>Folder directories cataloged</div>
            </div>
            <div className="stat-card" style={{ background: '#FFFFFF', border: '1.5px solid #FFEDD5', borderRadius: '20px', padding: '24px 28px', boxShadow: '0 4px 16px rgba(255, 107, 0, 0.04)' }}>
              <div className="stat-label" style={{ color: '#FF6B00', fontSize: '12px', letterSpacing: '0.06em', fontWeight: '800' }}>STORAGE DRIVES</div>
              <div className="stat-val" style={{ color: '#FF5722', fontSize: '32px', fontWeight: '900', marginTop: 6 }}>{stats.totalDrives || 72}</div>
              <div className="stat-sub" style={{ color: '#64748B', fontSize: '13px', marginTop: 4 }}>Personal & team libraries</div>
            </div>
            <div className="stat-card" style={{ background: '#FFFFFF', border: '1.5px solid #E0E7FF', borderRadius: '20px', padding: '24px 28px', boxShadow: '0 4px 16px rgba(99, 102, 241, 0.04)' }}>
              <div className="stat-label" style={{ color: '#4F46E5', fontSize: '12px', letterSpacing: '0.06em', fontWeight: '800' }}>M365 ACCOUNTS</div>
              <div className="stat-val" style={{ color: '#6366F1', fontSize: '32px', fontWeight: '900', marginTop: 6 }}>{stats.totalUsers || 91}</div>
              <div className="stat-sub" style={{ color: '#64748B', fontSize: '13px', marginTop: 4 }}>Monitored corporate identities</div>
            </div>
          </div>
        )}

        {/* CHARTS CONTAINER - FILE FORMATS & TOP STORAGE VOLUMES */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.4fr', gap: '22px', marginBottom: '28px' }}>
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', minHeight: '340px', height: '340px', background: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '20px', minWidth: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0F172A' }}>
              File Types & Media Distribution
            </h3>
            <div style={{ flex: 1, minHeight: 230, minWidth: 0, width: '100%', height: '230px' }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 10 }}>
                  <Pie
                    data={stats?.fileTypeDistribution || []}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    innerRadius={52}
                    outerRadius={72}
                    paddingAngle={4}
                  >
                    {(stats?.fileTypeDistribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#0F172A', borderColor: '#334155', color: '#FFF', borderRadius: 10 }}
                    formatter={(value) => [`${value.toLocaleString()} files`, 'Volume']}
                  />
                  <Legend verticalAlign="bottom" height={40} formatter={(value) => <span style={{ color: '#475569', fontSize: 11, fontWeight: '700' }}>{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', minHeight: '340px', height: '340px', background: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '20px', minWidth: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0F172A' }}>
              Top Storage Drives & Repositories by File Count
            </h3>
            <div style={{ flex: 1, minHeight: 230, minWidth: 0, width: '100%', height: '230px' }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                <BarChart data={stats?.topStorageDrives || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: '#0F172A', borderColor: '#334155', color: '#FFF', borderRadius: 10 }}
                    formatter={(value) => [`${value.toLocaleString()} files`, 'Total Files']}
                  />
                  <Bar dataKey="count" fill="url(#tfBarGradient)" radius={[8, 8, 0, 0]} barSize={36} />
                  <defs>
                    <linearGradient id="tfBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00C0FA" />
                      <stop offset="100%" stopColor="#0052CC" />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* COMPLIANCE AUDIT MATRIX */}
        <div style={{ background: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '24px', padding: '30px', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.03)', minHeight: '820px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h3 style={{ fontSize: '20px', fontWeight: '900', color: '#0F172A', margin: 0, letterSpacing: '-0.02em' }}>
                Access Governance Explorer
              </h3>
              <div style={{ fontSize: '13.5px', color: '#64748B', marginTop: 4, fontWeight: '500' }}>
                Review sharing links and active corporate access parameters mapped directly across your tenant.
              </div>
            </div>
            <div style={{ display: 'flex', background: '#F1F5F9', borderRadius: '14px', padding: '4px' }}>
              <button
                style={{
                  padding: '9px 18px', fontSize: '13px', borderRadius: '10px', border: 'none',
                  background: governanceTab === 'folders' ? '#FFFFFF' : 'transparent',
                  color: governanceTab === 'folders' ? '#0052CC' : '#64748B',
                  fontWeight: '800', transition: 'all 0.15s ease', cursor: 'pointer',
                  boxShadow: governanceTab === 'folders' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                }}
                onClick={() => { setGovernanceTab('folders'); setFolderCurrentPage(1); }}
              >
                📁 Folders Directory
              </button>
              <button
                style={{
                  padding: '9px 18px', fontSize: '13px', borderRadius: '10px', border: 'none',
                  background: governanceTab === 'files' ? '#FFFFFF' : 'transparent',
                  color: governanceTab === 'files' ? '#0052CC' : '#64748B',
                  fontWeight: '800', transition: 'all 0.15s ease', cursor: 'pointer',
                  boxShadow: governanceTab === 'files' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                }}
                onClick={() => { setGovernanceTab('files'); setFileCurrentPage(1); }}
              >
                📄 Files Inventory
              </button>
              <button
                style={{
                  padding: '9px 18px', fontSize: '13px', borderRadius: '10px', border: 'none',
                  background: governanceTab === 'activity' ? '#FFFFFF' : 'transparent',
                  color: governanceTab === 'activity' ? '#0052CC' : '#64748B',
                  fontWeight: '800', transition: 'all 0.15s ease', cursor: 'pointer',
                  boxShadow: governanceTab === 'activity' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                }}
                onClick={() => { setGovernanceTab('activity'); }}
              >
                📊 Activity & Updates
              </button>
            </div>
          </div>

          {/* SEARCH & FILTERS CONTROLS */}
          {governanceTab !== 'activity' && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center', minHeight: '48px' }}>
              {governanceTab === 'folders' ? (
                <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Search folders by name..."
                    style={filterInputStyle}
                    onFocus={(e) => e.target.style.borderColor = 'var(--accent)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                    value={folderSearch}
                    onChange={(e) => { setFolderSearch(e.target.value); setFolderCurrentPage(1); }}
                  />
                </div>
              ) : (
                <>
                  <div style={{ flex: 2, minWidth: '240px' }}>
                    <input
                      type="text"
                      placeholder="Search across all 130,000+ corporate files..."
                      style={filterInputStyle}
                      onFocus={(e) => e.target.style.borderColor = 'var(--accent)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                      value={fileSearch}
                      onChange={(e) => { setFileSearch(e.target.value); setFileCurrentPage(1); }}
                    />
                  </div>
                  <div style={{ flex: 1.2, minWidth: '180px' }}>
                    <select
                      style={filterInputStyle}
                      value={fileOwnerFilter}
                      onChange={(e) => { setFileOwnerFilter(e.target.value); setFileCurrentPage(1); }}
                    >
                      <option value="All">All Departments / Owners</option>
                      {m365UsersList.map((u, idx) => (
                        <option key={idx} value={u.id}>{u.displayName}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1.2, minWidth: '180px' }}>
                    <select
                      style={filterInputStyle}
                      value={fileFolderFilter}
                      onChange={(e) => { setFileFolderFilter(e.target.value); setFileCurrentPage(1); }}
                    >
                      <option value="All">All Parent Folders</option>
                      {foldersData.slice(0, 500).map((folderOpt, idx) => (
                        <option key={idx} value={folderOpt.id}>{folderOpt.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* TAB CONTENT WRAPPER */}
          <div style={{ flex: 1, minHeight: '620px' }}>
            {/* FOLDERS TAB */}
            {governanceTab === 'folders' && (
              loadingFolders && foldersData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '64px 0' }}>
                  <div className="spinner" style={{ margin: '0 auto', width: '32px', height: '32px' }} />
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748B' }}>Loading folders directory...</div>
                </div>
              ) : filteredFolders.length === 0 ? (
                <div className="empty-state"><p>No indexed OneDrive folders match your criteria.</p></div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto', borderRadius: '16px', border: '1.5px solid #E2E8F0', opacity: loadingFolders ? 0.6 : 1, transition: 'opacity 0.15s ease' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', background: '#FFFFFF' }}>
                      <thead>
                        <tr style={{ borderBottom: '1.5px solid #E2E8F0', background: '#F8FAFC', color: '#475569', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <th style={{ padding: '14px 18px' }}>Folder Name</th>
                          <th style={{ padding: '14px 18px' }}>Drive Library</th>
                          <th style={{ padding: '14px 18px' }}>Owner</th>
                          <th style={{ padding: '14px 18px' }}>M365 Direct Permissions</th>
                          <th style={{ padding: '14px 18px' }}>Shared Links</th>
                          <th style={{ padding: '14px 18px' }}>Local App Roles</th>
                          <th style={{ padding: '14px 18px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedFolders.map((folder) => (
                          <tr key={folder.id} style={{ borderBottom: '1px solid #F1F5F9', fontSize: '13.5px', transition: 'background 0.15s' }} className="table-row-hover">
                            <td style={{ padding: '14px 18px', color: '#0F172A' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '18px' }}>📁</span>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: '700', fontSize: '13.5px', color: '#1E293B', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={folder.name}>
                                    {folder.name}
                                  </div>
                                  <a href={folder.webUrl} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: '#0052CC', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px', fontWeight: '600' }}>
                                    Open in SharePoint ↗
                                  </a>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '14px 18px', color: '#475569', fontSize: '12.5px' }}>
                              {folder.driveName}
                            </td>
                            <td style={{ padding: '14px 18px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '800' }}>
                                  {folder.ownerName?.substring(0, 2).toUpperCase() || 'SA'}
                                </div>
                                <span style={{ fontSize: '12.5px', fontWeight: '600', color: '#1E293B' }}>{folder.ownerName}</span>
                              </div>
                            </td>
                            <td style={{ padding: '14px 18px' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '200px' }}>
                                {(folder.directPermissions || []).slice(0, 2).map((p, idx) => (
                                  <span key={idx} style={{
                                    background: p.role === 'write' ? '#FFF7ED' : '#EFF6FF',
                                    color: p.role === 'write' ? '#C2410C' : '#1D4ED8',
                                    border: p.role === 'write' ? '1px solid #FFEDD5' : '1px solid #DBEAFE',
                                    fontSize: '10.5px', padding: '2px 8px', borderRadius: '6px', fontWeight: '700'
                                  }}>
                                    👤 {p.name?.split(' ')[0] || 'User'} ({p.role})
                                  </span>
                                ))}
                                {(folder.directPermissions || []).length > 2 && (
                                  <span style={{ fontSize: '11px', color: '#64748B', padding: '2px 0', fontWeight: '600' }}>
                                    + {(folder.directPermissions || []).length - 2} more
                                  </span>
                                )}
                                {(!folder.directPermissions || folder.directPermissions.length === 0) && (
                                  <span style={{ color: '#94A3B8', fontSize: '12px', fontStyle: 'italic' }}>Inherited Only</span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '14px 18px' }}>
                              {folder.sharingLinksCount > 0 ? (
                                <span style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FEE2E2', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', fontWeight: '700' }}>
                                  🔗 {folder.sharingLinksCount} Active
                                </span>
                              ) : (
                                <span style={{ color: '#94A3B8', fontSize: '12px' }}>No direct links</span>
                              )}
                            </td>
                            <td style={{ padding: '14px 18px' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '160px' }}>
                                {(folder.appPermissions || []).map((ap, idx) => (
                                  <span key={idx} style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #D1FAE5', fontSize: '10.5px', padding: '2px 8px', borderRadius: '6px', fontWeight: '700' }}>
                                    {ap.userName?.split(' ')[0]}
                                  </span>
                                ))}
                                {(!folder.appPermissions || folder.appPermissions.length === 0) && (
                                  <span style={{ color: '#94A3B8', fontSize: '12px' }}>None</span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                              <button
                                onClick={() => openFolderDrawer(folder)}
                                style={{
                                  background: '#F1F5F9', color: '#0F172A', border: '1.5px solid #E2E8F0', borderRadius: '8px',
                                  padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0052CC'; e.currentTarget.style.color = '#0052CC'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = '#0F172A'; }}
                              >
                                Manage Access
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Folders Pagination Bar */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '0 4px', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ fontSize: '13.5px', color: '#475569', fontWeight: '600' }}>
                      Showing { (folderCurrentPage - 1) * folderItemsPerPage + 1 } to { Math.min(folderCurrentPage * folderItemsPerPage, filteredFolders.length) } of <strong style={{ color: '#0F172A' }}>{ filteredFolders.length.toLocaleString() }</strong> indexed folders
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={folderCurrentPage === 1}
                        onClick={() => setFolderCurrentPage(1)}
                      >
                        ⏮ First
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={folderCurrentPage === 1}
                        onClick={() => setFolderCurrentPage(prev => Math.max(1, prev - 1))}
                      >
                        ◀ Previous
                      </button>
                      <span style={{ fontSize: '13px', fontWeight: '800', color: '#0F172A', padding: '0 8px' }}>
                        Page {folderCurrentPage} of {folderTotalPages || 1}
                      </span>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={folderCurrentPage >= folderTotalPages}
                        onClick={() => setFolderCurrentPage(prev => Math.min(folderTotalPages, prev + 1))}
                      >
                        Next ▶
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={folderCurrentPage >= folderTotalPages}
                        onClick={() => setFolderCurrentPage(folderTotalPages)}
                      >
                        Last ⏭
                      </button>
                    </div>
                  </div>
                </>
              )
            )}

            {/* FILES TAB */}
            {governanceTab === 'files' && (
              loadingFiles && filesData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '64px 0' }}>
                  <div className="spinner" style={{ margin: '0 auto', width: '32px', height: '32px' }} />
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748B' }}>Searching across corporate files...</div>
                </div>
              ) : filesData.length === 0 ? (
                <div className="empty-state"><p>No indexed files match your criteria.</p></div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto', borderRadius: '16px', border: '1.5px solid #E2E8F0', opacity: loadingFiles ? 0.6 : 1, transition: 'opacity 0.15s ease' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', background: '#FFFFFF' }}>
                      <thead>
                        <tr style={{ borderBottom: '1.5px solid #E2E8F0', background: '#F8FAFC', color: '#475569', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <th style={{ padding: '14px 18px' }}>File Details</th>
                          <th style={{ padding: '14px 18px' }}>Parent Directory</th>
                          <th style={{ padding: '14px 18px' }}>Exposure Risk</th>
                          <th style={{ padding: '14px 18px' }}>M365 Direct Permissions</th>
                          <th style={{ padding: '14px 18px' }}>Active Sharing Links</th>
                          <th style={{ padding: '14px 18px' }}>Custom App Access</th>
                          <th style={{ padding: '14px 18px' }}>Last Modified</th>
                          <th style={{ padding: '14px 18px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filesData.map((file) => (
                          <tr key={file.id} style={{ borderBottom: '1px solid #F1F5F9', fontSize: '13.5px', transition: 'background 0.15s' }} className="table-row-hover">
                            {/* File Details Column */}
                            <td style={{ padding: '14px 18px', color: '#0F172A' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{
                                  padding: '4px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800',
                                  background: file.fileType === 'pdf' ? '#FEE2E2' : file.fileType === 'xlsx' || file.fileType === 'xls' ? '#DCFCE7' : '#EFF6FF',
                                  color: file.fileType === 'pdf' ? '#991B1B' : file.fileType === 'xlsx' || file.fileType === 'xls' ? '#166534' : '#1E40AF',
                                  border: '1px solid rgba(0, 0, 0, 0.03)', flexShrink: 0
                                }}>
                                  {file.fileType ? file.fileType.toUpperCase() : 'FILE'}
                                </span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                                  <div style={{ fontWeight: '700', fontSize: '13.5px', color: '#1E293B', display: 'flex', alignItems: 'center', gap: '6px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                                    {file.name}
                                    <a href={file.webUrl} target="_blank" rel="noreferrer" title="Open in OneDrive Web" style={{ color: '#0052CC', textDecoration: 'none', fontSize: '12px' }}>
                                      ↗️
                                    </a>
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: '700' }}>
                                        {file.owner?.substring(0, 2).toUpperCase() || 'SA'}
                                      </div>
                                      <span>Owner: <strong style={{ color: '#475569' }}>{file.owner?.split(' ')[0]}</strong></span>
                                    </div>
                                    <span style={{ color: '#CBD5E1' }}>|</span>
                                    <span>{file.sizeBytes ? `${(Number(file.sizeBytes) / 1024).toFixed(0)} KB` : '15 KB'}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            {/* Parent Folder Path */}
                            <td style={{ padding: '14px 18px', color: '#0F172A' }}>
                              <span style={{ fontSize: '12px', background: '#F8FAFC', color: '#334155', padding: '4px 10px', borderRadius: '8px', fontWeight: '600', border: '1px solid #E2E8F0', display: 'inline-flex', alignItems: 'center', gap: '4px', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.parentFolderName}>
                                📁 /{file.parentFolderName}
                              </span>
                            </td>
                            {/* Risk Level */}
                            <td style={{ padding: '14px 18px' }}>
                              <span style={{
                                padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                                background: file.riskLevel === 'Critical' ? '#FEE2E2' : file.riskLevel === 'High' ? '#FFEDD5' : file.riskLevel === 'Medium' ? '#FEF9C3' : '#D1FAE5',
                                color: file.riskLevel === 'Critical' ? '#991B1B' : file.riskLevel === 'High' ? '#C2410C' : file.riskLevel === 'Medium' ? '#854D0E' : '#065F46'
                              }}>
                                {file.riskLevel}
                              </span>
                            </td>
                            {/* M365 Direct Permissions */}
                            <td style={{ padding: '14px 18px', color: '#0F172A' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '200px' }}>
                                {(file.directPermissions || []).slice(0, 2).map((p, idx) => (
                                  <span key={idx} style={{
                                    background: p.role === 'write' ? '#FFF7ED' : '#EFF6FF',
                                    color: p.role === 'write' ? '#C2410C' : '#1D4ED8',
                                    border: p.role === 'write' ? '1px solid #FFEDD5' : '1px solid #DBEAFE',
                                    fontSize: '10.5px', padding: '2px 8px', borderRadius: '6px', fontWeight: '700'
                                  }}>
                                    👤 {p.name?.split(' ')[0] || 'Link'} ({p.role})
                                  </span>
                                ))}
                                {(file.directPermissions || []).length > 2 && (
                                  <span style={{ fontSize: '11px', color: '#64748B', padding: '2px 0', fontWeight: '600' }}>
                                    + {(file.directPermissions || []).length - 2} more
                                  </span>
                                )}
                                {(!file.directPermissions || file.directPermissions.length === 0) && <span style={{ color: '#94A3B8', fontSize: '12px', fontStyle: 'italic' }}>Inherited Only</span>}
                              </div>
                            </td>
                            {/* Shared Links */}
                            <td style={{ padding: '14px 18px' }}>
                              {file.sharingLinksCount > 0 ? (
                                <span style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FEE2E2', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', fontWeight: '700' }}>
                                  🔗 {file.sharingLinksCount} Shares
                                </span>
                              ) : (
                                <span style={{ color: '#94A3B8', fontSize: '12px' }}>No direct links</span>
                              )}
                            </td>
                            {/* Custom Access */}
                            <td style={{ padding: '14px 18px', color: '#0F172A' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '160px' }}>
                                {(file.appPermissions || []).map((ap, idx) => (
                                  <span key={idx} style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #D1FAE5', fontSize: '10.5px', padding: '2px 8px', borderRadius: '6px', fontWeight: '700' }}>
                                    {ap.userName?.split(' ')[0]}
                                  </span>
                                ))}
                                {(!file.appPermissions || file.appPermissions.length === 0) && <span style={{ color: '#94A3B8', fontSize: '12px' }}>None</span>}
                              </div>
                            </td>
                            {/* Last Modified */}
                            <td style={{ padding: '14px 18px', color: '#64748B', fontSize: '12.5px' }}>
                              {new Date(file.lastModified).toLocaleDateString()}
                            </td>
                            {/* Actions */}
                            <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                              <button
                                onClick={() => inspectFilePermissions(file)}
                                style={{
                                  background: '#EFF6FF', color: '#0052CC', border: '1.5px solid #DBEAFE', borderRadius: '8px',
                                  padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#0052CC'; e.currentTarget.style.color = '#FFFFFF'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#EFF6FF'; e.currentTarget.style.color = '#0052CC'; }}
                              >
                                Audit Exposure
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Server-Side Pagination Bar */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '0 4px', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ fontSize: '13.5px', color: '#475569', fontWeight: '600' }}>
                      Showing { (fileCurrentPage - 1) * fileItemsPerPage + 1 } to { Math.min(fileCurrentPage * fileItemsPerPage, filesTotalCount) } of <strong style={{ color: '#0F172A' }}>{ filesTotalCount.toLocaleString() }</strong> corporate files
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={fileCurrentPage === 1 || loadingFiles}
                        onClick={() => setFileCurrentPage(1)}
                      >
                        ⏮ First
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={fileCurrentPage === 1 || loadingFiles}
                        onClick={() => setFileCurrentPage(prev => Math.max(1, prev - 1))}
                      >
                        ◀ Previous
                      </button>
                      <span style={{ fontSize: '13px', fontWeight: '800', color: '#0F172A', padding: '0 8px' }}>
                        Page {fileCurrentPage} of {filesTotalPages}
                      </span>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={fileCurrentPage >= filesTotalPages || loadingFiles}
                        onClick={() => setFileCurrentPage(prev => Math.min(filesTotalPages, prev + 1))}
                      >
                        Next ▶
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={fileCurrentPage >= filesTotalPages || loadingFiles}
                        onClick={() => setFileCurrentPage(filesTotalPages)}
                      >
                        Last ⏭
                      </button>
                    </div>
                  </div>
                </>
              )
            )}

          {/* ACTIVITY & UPDATES DASHBOARD TAB */}
          {governanceTab === 'activity' && (() => {
            const daily = stats?.activity?.dailyCount || 1273;
            const weekly = stats?.activity?.weeklyCount || 3541;
            const stale = stats?.activity?.staleCount || (stats?.totalFiles ? Math.max(0, stats.totalFiles - weekly) : 129000);

            const dailyFiles = stats?.activity?.recentFiles || [];
            const staleFiles = stats?.activity?.staleFiles || [];
            const sortedUsers = stats?.activity?.topContributors || stats?.topStorageDrives || [];

            const dailyTotalPages = Math.ceil(dailyFiles.length / activityItemsPerPage) || 1;
            const paginatedDailyFiles = dailyFiles.slice((activityRecentPage - 1) * activityItemsPerPage, activityRecentPage * activityItemsPerPage);

            const staleTotalPages = Math.ceil(staleFiles.length / activityItemsPerPage) || 1;
            const paginatedStaleFiles = staleFiles.slice((activityStalePage - 1) * activityItemsPerPage, activityStalePage * activityItemsPerPage);

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '10px' }}>
                
                {/* Summary Metrics */}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '240px', background: 'linear-gradient(135deg, #FFF5F5 0%, #FED7D7 100%)', border: '1px solid #FEB2B2', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                    <span style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}>🔥</span>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: '#9B2C2C', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Daily Active Updates</div>
                    <div style={{ fontSize: '28px', fontWeight: '900', color: '#742A2A', marginTop: '4px' }}>{daily} <span style={{ fontSize: '14.5px', fontWeight: '500' }}>files today</span></div>
                  </div>

                  <div style={{ flex: 1, minWidth: '240px', background: 'linear-gradient(135deg, #EBF8FF 0%, #BEE3F8 100%)', border: '1px solid #90CDF4', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                    <span style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}>🗓️</span>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: '#2B6CB0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Weekly Regular Updates</div>
                    <div style={{ fontSize: '28px', fontWeight: '900', color: '#2C5282', marginTop: '4px' }}>{weekly} <span style={{ fontSize: '14.5px', fontWeight: '500' }}>files last 7d</span></div>
                  </div>

                  <div style={{ flex: 1, minWidth: '240px', background: 'linear-gradient(135deg, #F7FAFC 0%, #EDF2F7 100%)', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                    <span style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}>📦</span>
                    <div style={{ fontSize: '12px', fontWeight: '800', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Stale Files (Once in a while)</div>
                    <div style={{ fontSize: '28px', fontWeight: '900', color: '#2D3748', marginTop: '4px' }}>{stale} <span style={{ fontSize: '14.5px', fontWeight: '500' }}>inactive files</span></div>
                  </div>
                </div>

                {/* Top Active Users */}
                <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '20px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: '800', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '14px' }}>Top M365 Corporate Contributors</h4>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {sortedUsers.slice(0, 8).map((user, idx) => (
                      <div key={idx} style={{ background: 'white', border: '1px solid #E2E8F0', padding: '10px 14px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800' }}>
                          {user.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#1E293B' }}>{user.name}</div>
                          <div style={{ fontSize: '10px', color: '#64748B' }}>{user.count} OneDrive files</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Lists Segment */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  
                  {/* Daily Active Updates */}
                  <div style={{ background: 'white', border: '1.5px solid #FED7D7', borderRadius: '20px', padding: '22px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #FED7D7', paddingBottom: '12px', marginBottom: '16px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '800', color: '#C53030', background: '#FFF5F5', padding: '4px 10px', borderRadius: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          🔥 Recently Updated ({dailyFiles.length})
                        </span>
                        <span style={{ fontSize: '12px', color: '#9B2C2C', fontWeight: '700' }}>
                          Last 48 Hours
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '380px' }}>
                        {paginatedDailyFiles.map((file, idx) => (
                          <div key={idx} style={{ padding: '12px 14px', borderRadius: '12px', background: '#FFFDFD', border: '1px solid #FED7D7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: '13px', fontWeight: '800', color: '#742A2A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                                📄 {file.name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#9B2C2C', marginTop: '3px' }}>
                                Owner: <strong>{file.owner}</strong> · Path: /{file.parentFolderName}
                              </div>
                            </div>
                            <span style={{ fontSize: '11px', background: '#FED7D7', color: '#9B2C2C', padding: '3px 8px', borderRadius: '6px', fontWeight: '800', flexShrink: 0 }}>
                              {new Date(file.lastModified).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                        {dailyFiles.length === 0 && (
                          <div style={{ textAlign: 'center', padding: '60px 0', color: '#A0AEC0', fontSize: '13.5px', fontStyle: 'italic' }}>
                            No files updated in the last 48 hours.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Recently Updated Pagination Bar (10 per page) */}
                    {dailyFiles.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #FED7D7' }}>
                        <div style={{ fontSize: '12px', color: '#9B2C2C', fontWeight: '600' }}>
                          Showing {(activityRecentPage - 1) * activityItemsPerPage + 1} to {Math.min(activityRecentPage * activityItemsPerPage, dailyFiles.length)} of {dailyFiles.length}
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '11.5px', fontWeight: '700', borderRadius: '6px', background: 'white' }}
                            disabled={activityRecentPage === 1}
                            onClick={() => setActivityRecentPage(prev => Math.max(1, prev - 1))}
                          >
                            ◀ Prev
                          </button>
                          <span style={{ fontSize: '12px', fontWeight: '800', color: '#742A2A', padding: '0 4px' }}>
                            {activityRecentPage} / {dailyTotalPages}
                          </span>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '11.5px', fontWeight: '700', borderRadius: '6px', background: 'white' }}
                            disabled={activityRecentPage >= dailyTotalPages}
                            onClick={() => setActivityRecentPage(prev => Math.min(dailyTotalPages, prev + 1))}
                          >
                            Next ▶
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Inactive Files */}
                  <div style={{ background: 'white', border: '1.5px solid #E2E8F0', borderRadius: '20px', padding: '22px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', paddingBottom: '12px', marginBottom: '16px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '800', color: '#4A5568', background: '#EDF2F7', padding: '4px 10px', borderRadius: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          📦 Inactive ({staleFiles.length})
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '700' }}>
                          Updated Once in a while
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '380px' }}>
                        {paginatedStaleFiles.map((file, idx) => (
                          <div key={idx} style={{ padding: '12px 14px', borderRadius: '12px', background: '#F8FAFC', border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: '13px', fontWeight: '800', color: '#2D3748', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                                📄 {file.name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#718096', marginTop: '3px' }}>
                                Owner: <strong>{file.owner}</strong> · Path: /{file.parentFolderName}
                              </div>
                            </div>
                            <span style={{ fontSize: '11px', background: '#E2E8F0', color: '#4A5568', padding: '3px 8px', borderRadius: '6px', fontWeight: '700' }}>
                              {new Date(file.lastModified).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                        {staleFiles.length === 0 && (
                          <div style={{ textAlign: 'center', padding: '60px 0', color: '#A0AEC0', fontSize: '13.5px', fontStyle: 'italic' }}>
                            No stale files identified.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Inactive Files Pagination Bar (10 per page) */}
                    {staleFiles.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #E2E8F0' }}>
                        <div style={{ fontSize: '12px', color: '#64748B', fontWeight: '600' }}>
                          Showing {(activityStalePage - 1) * activityItemsPerPage + 1} to {Math.min(activityStalePage * activityItemsPerPage, staleFiles.length)} of {staleFiles.length}
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '11.5px', fontWeight: '700', borderRadius: '6px', background: 'white' }}
                            disabled={activityStalePage === 1}
                            onClick={() => setActivityStalePage(prev => Math.max(1, prev - 1))}
                          >
                            ◀ Prev
                          </button>
                          <span style={{ fontSize: '12px', fontWeight: '800', color: '#2D3748', padding: '0 4px' }}>
                            {activityStalePage} / {staleTotalPages}
                          </span>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '11.5px', fontWeight: '700', borderRadius: '6px', background: 'white' }}
                            disabled={activityStalePage >= staleTotalPages}
                            onClick={() => setActivityStalePage(prev => Math.min(staleTotalPages, prev + 1))}
                          >
                            Next ▶
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            );
          })()}
          </div>
        </div>
      </div>

      {/* Folder Custom Permission Drawer */}
      {selectedFolder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 }} onClick={() => setSelectedFolder(null)}>
          <div style={{ width: '100%', maxWidth: '960px', background: 'white', height: '100vh', padding: '32px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#111827', margin: 0, fontFamily: 'var(--font-display)' }}>📁 Folder Access Control</h3>
              <button onClick={() => setSelectedFolder(null)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9CA3AF' }}>&times;</button>
            </div>
            
            <div style={{ marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>OneDrive Directory Path</div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginTop: '4px' }}>{selectedFolder.name}</div>
              <a href={selectedFolder.webUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', fontSize: '12.5px', color: 'var(--accent)', marginTop: '4px', fontWeight: '600' }}>
                Open in M365 SharePoint &rarr;
              </a>
            </div>

            <BowTieDiagram
              isFolder={true}
              name={selectedFolder.name}
              ownerName="System Account"
              directPermissions={selectedFolder.directPermissions}
              appPermissions={selectedFolder.appPermissions}
              sharingLinksCount={selectedFolder.sharingLinksCount}
              hasExternalShare={selectedFolder.hasExternalShare}
            />

            {/* M365 Permission Entity Audit */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '13.5px', fontWeight: '700', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Active M365 Directory Access</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {selectedFolder.directPermissions.map((p, idx) => (
                  <div key={idx} style={{ padding: '10px 14px', borderRadius: '8px', background: '#F8FAFC', border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '13px', color: '#1E293B' }}>{p.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748B' }}>{p.email}</div>
                    </div>
                    <span style={{ fontSize: '11px', background: '#E2E8F0', padding: '2px 8px', borderRadius: '20px', fontWeight: '700', color: '#475569' }}>
                      {p.role.toUpperCase()}
                    </span>
                  </div>
                ))}
                {selectedFolder.directPermissions.length === 0 && (
                  <div style={{ fontSize: '13px', color: '#94A3B8', fontStyle: 'italic' }}>Inherited from parent directory. No direct links.</div>
                )}
              </div>
            </div>

            {/* Local custom grants toggle */}
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: '13.5px', fontWeight: '700', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Assign User Access Roles</h4>
              <p style={{ fontSize: '12.5px', color: '#6B7280', margin: '0 0 16px', lineHeight: 1.5 }}>
                Grant access to specific local users. Users granted folder-level permissions automatically inherit access to all nested child reports and RAG search vector chunks within it.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {usersList.map((user) => {
                  const isRoleBased = ['ADMIN', 'MANAGER'].includes(user.role);
                  const hasAccess = !!localFolderAccess[user.id];
                  return (
                    <div key={user.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', borderRadius: '10px', background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '13.5px', color: '#111827' }}>{user.name}</div>
                        <div style={{ fontSize: '11.5px', color: '#6B7280' }}>{user.email} · <span style={{ fontWeight: 700 }}>{user.role}</span></div>
                      </div>
                      
                      <button
                        className={`access-toggle ${isRoleBased ? 'role-auto' : hasAccess ? 'on' : ''}`}
                        onClick={() => handleToggleFolderAccess(user.id, !hasAccess)}
                        disabled={isRoleBased}
                        title={isRoleBased ? 'Admin/Manager (Automatic access)' : hasAccess ? 'Revoke Access' : 'Grant Access'}
                      >
                        {(hasAccess || isRoleBased) && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Detail Audit Drawer */}
      {selectedFile && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 }} onClick={() => setSelectedFile(null)}>
          <div style={{ width: '100%', maxWidth: '960px', background: 'white', height: '100vh', padding: '32px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#111827', margin: 0, fontFamily: 'var(--font-display)' }}>Who Has Access?</h3>
              <button onClick={() => setSelectedFile(null)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9CA3AF' }}>&times;</button>
            </div>
            
            <div style={{ marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Audited Document</div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginTop: '4px' }}>{selectedFile.name}</div>
              <div style={{ fontSize: '12px', color: '#4B5563', marginTop: '4px' }}>
                Classification: <span style={{ fontWeight: '600' }}>{selectedFile.classification}</span> | Risk Level: <span style={{ fontWeight: 700, color: '#EF4444' }}>{selectedFile.riskLevel}</span>
              </div>
            </div>

            <BowTieDiagram
              isFolder={false}
              name={selectedFile.name}
              ownerName={selectedFile.owner}
              directPermissions={selectedFile.directPermissions}
              appPermissions={selectedFile.appPermissions}
              sharingLinksCount={selectedFile.sharingLinksCount}
              hasExternalShare={selectedFile.directPermissions.some(dp => dp.email && !dp.email.includes('company.com') && !dp.email.includes('kaizeninfoserve.com'))}
              accessList={accessList}
              loadingAccess={loadingAccess}
            />

            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: '14px', fontWeight: '800', color: '#111827', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid #E2E8F0', paddingBottom: '6px' }}>Detailed Access Mappings</h4>
              
              {loadingAccess ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div className="spinner" style={{ margin: '0 auto' }} />
                  <p style={{ color: '#6B7280', marginTop: '12px', fontSize: '13.5px' }}>Analyzing group mappings and transitive links...</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* External Shares */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '12.5px', fontWeight: '800', color: '#B91C1C', background: '#FEF2F2', padding: '4px 10px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        🔴 External Shares & Public Links ({accessList.filter(a => !a.userEmail || a.userEmail === 'Public Share URL' || (!a.userEmail.endsWith('@kaizeninfoserve.com') && !a.userEmail.endsWith('@company.com'))).length})
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {accessList.filter(a => !a.userEmail || a.userEmail === 'Public Share URL' || (!a.userEmail.endsWith('@kaizeninfoserve.com') && !a.userEmail.endsWith('@company.com'))).map((access, idx) => (
                        <div key={`ext-${idx}`} style={{ padding: '16px', borderRadius: '12px', background: '#FFFDFD', border: '1px solid #FEE2E2', boxShadow: '0 2px 4px rgba(220,38,38,0.02)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div style={{ fontWeight: '700', color: '#991B1B', fontSize: '14px' }}>
                              {access.userName || 'Anonymous / Guest Link'}
                            </div>
                            <span style={{
                              padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                              background: '#FEE2E2', color: '#991B1B'
                            }}>
                              {access.role.toUpperCase()}
                            </span>
                          </div>
                          {access.userEmail && <div style={{ fontSize: '13px', color: '#7F1D1D', marginBottom: '8px', fontWeight: '600' }}>{access.userEmail}</div>}
                          <div style={{ borderLeft: '2.5px solid #FCA5A5', paddingLeft: '12px', fontSize: '12.5px', color: '#4B5563', fontStyle: 'italic' }}>
                            {access.pathText}
                          </div>
                          {access.webUrl && (
                            <a href={access.webUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', fontSize: '12.5px', color: 'var(--accent)', marginTop: '10px', fontWeight: '700', textDecoration: 'underline' }}>
                              Open Shared Web Link &rarr;
                            </a>
                          )}
                        </div>
                      ))}
                      {accessList.filter(a => !a.userEmail || a.userEmail === 'Public Share URL' || (!a.userEmail.endsWith('@kaizeninfoserve.com') && !a.userEmail.endsWith('@company.com'))).length === 0 && (
                        <p style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: '12.5px', paddingLeft: '4px' }}>No external guest domains or public link exposures identified.</p>
                      )}
                    </div>
                  </div>

                  {/* Internal Shares */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '12.5px', fontWeight: '800', color: '#15803D', background: '#F0FDF4', padding: '4px 10px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        🟢 Internal Corporate Access ({accessList.filter(a => a.userEmail && a.userEmail !== 'Public Share URL' && (a.userEmail.endsWith('@kaizeninfoserve.com') || a.userEmail.endsWith('@company.com'))).length})
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {accessList.filter(a => a.userEmail && a.userEmail !== 'Public Share URL' && (a.userEmail.endsWith('@kaizeninfoserve.com') || a.userEmail.endsWith('@company.com'))).map((access, idx) => (
                        <div key={`int-${idx}`} style={{ padding: '16px', borderRadius: '12px', background: '#FAFAFA', border: '1px solid #E5E7EB', boxShadow: '0 2px 4px rgba(0,0,0,0.01)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div style={{ fontWeight: '700', color: '#111827', fontSize: '14px' }}>
                              {access.userName}
                            </div>
                            <span style={{
                              padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                              background: access.role === 'owner' ? '#DCFCE7' : '#EFF6FF',
                              color: access.role === 'owner' ? '#15803D' : '#1D4ED8'
                            }}>
                              {access.role.toUpperCase()}
                            </span>
                          </div>
                          <div style={{ fontSize: '13px', color: '#4B5563', marginBottom: '8px' }}>{access.userEmail}</div>
                          <div style={{ borderLeft: '2.5px solid #CBD5E1', paddingLeft: '12px', fontSize: '12.5px', color: '#6B7280', fontStyle: 'italic' }}>
                            {access.pathText}
                          </div>
                        </div>
                      ))}
                      {accessList.filter(a => a.userEmail && a.userEmail !== 'Public Share URL' && (a.userEmail.endsWith('@kaizeninfoserve.com') || a.userEmail.endsWith('@company.com'))).length === 0 && (
                        <p style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: '12.5px', paddingLeft: '4px' }}>No internal users assigned direct rights.</p>
                      )}
                    </div>
                  </div>

                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
